import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

// Saudi Arabia outline: a real (simplified) boundary polygon projected into
// the viewBox and smoothed into a curved path so it reads as a coastline
// rather than a faceted polygon.
const LON_MIN = 34;
const LON_MAX = 56;
const LAT_MIN = 16;
const LAT_MAX = 32.3;
const MARGIN = 16;
const VB_W = 640;
const VB_H = 480;

const project = ([lon, lat]) => [
  MARGIN + ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * (VB_W - MARGIN * 2),
  MARGIN + ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * (VB_H - MARGIN * 2),
];

// Real Saudi Arabia boundary (simplified Natural Earth polygon, via the
// public johan/world.geo.json dataset), traced clockwise from the Jizan /
// Red Sea coast up through the Gulf of Aqaba, across the northern border,
// down the Gulf coast, and back along the Empty Quarter / Yemen border.
const KSA_BOUNDARY = [
  [42.779332, 16.347891], [42.649573, 16.774635], [42.347989, 17.075806], [42.270888, 17.474722],
  [41.754382, 17.833046], [41.221391, 18.6716], [40.939341, 19.486485], [40.247652, 20.174635],
  [39.801685, 20.338862], [39.139399, 21.291905], [39.023696, 21.986875], [39.066329, 22.579656],
  [38.492772, 23.688451], [38.02386, 24.078686], [37.483635, 24.285495], [37.154818, 24.858483],
  [37.209491, 25.084542], [36.931627, 25.602959], [36.639604, 25.826228], [36.249137, 26.570136],
  [35.640182, 27.37652], [35.130187, 28.063352], [34.632336, 28.058546], [34.787779, 28.607427],
  [34.83222, 28.957483], [34.956037, 29.356555], [36.068941, 29.197495], [36.501214, 29.505254],
  [36.740528, 29.865283], [37.503582, 30.003776], [37.66812, 30.338665], [37.998849, 30.5085],
  [37.002166, 31.508413], [39.004886, 32.010217], [39.195468, 32.161009], [40.399994, 31.889992],
  [41.889981, 31.190009], [44.709499, 29.178891], [46.568713, 29.099025], [47.459822, 29.002519],
  [47.708851, 28.526063], [48.416094, 28.552004], [48.807595, 27.689628], [49.299554, 27.461218],
  [49.470914, 27.109999], [50.152422, 26.689663], [50.212935, 26.277027], [50.113303, 25.943972],
  [50.239859, 25.60805], [50.527387, 25.327808], [50.660557, 24.999896], [50.810108, 24.754743],
  [51.112415, 24.556331], [51.389608, 24.627386], [51.579519, 24.245497], [51.617708, 24.014219],
  [52.000733, 23.001154], [55.006803, 22.496948], [55.208341, 22.70833], [55.666659, 22.000001],
  [54.999982, 19.999994], [52.00001, 19.000003], [49.116672, 18.616668], [48.183344, 18.166669],
  [47.466695, 17.116682], [47.000005, 16.949999], [46.749994, 17.283338], [46.366659, 17.233315],
  [45.399999, 17.333335], [45.216651, 17.433329], [44.062613, 17.410359], [43.791519, 17.319977],
  [43.380794, 17.579987], [43.115798, 17.08844], [43.218375, 16.66689],
];

// Catmull-Rom -> cubic Bezier, closed loop, for a smooth coastline.
function smoothClosedPath(points) {
  const pts = points.map(project);
  const n = pts.length;
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)} `;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)} `;
  }
  return `${d}Z`;
}

const KSA_OUTLINE = smoothClosedPath(KSA_BOUNDARY);

const STOPS = [
  { id: 'jeddah', name: 'Jeddah', date: 'Oct 26', pos: [39.2, 21.5], nudge: [16, -6] },
  { id: 'riyadh', name: 'Riyadh', date: 'Oct 27', pos: [46.7, 24.7], nudge: [0, 20] },
  { id: 'dammam', name: 'Dammam', date: 'Oct 28', pos: [50.2, 26.2], nudge: [-22, 6] },
].map((stop) => {
  const [px, py] = project(stop.pos);
  return { ...stop, x: px + stop.nudge[0], y: py + stop.nudge[1] };
});

// Material-style map-pin outline (24x24 box, tip at 12,22.5) used for every
// city marker — gives real pins with a glossy body instead of flat dots.
const PIN_PATH =
  'M12 1.5C7.86 1.5 4.5 4.86 4.5 9c0 5.63 7.5 13.5 7.5 13.5S19.5 14.63 19.5 9c0-4.14-3.36-7.5-7.5-7.5zm0 10.13A2.63 2.63 0 1 1 12 6.38a2.63 2.63 0 0 1 0 5.25z';
const PIN_SCALE = 1.05;
const pinTransform = (x, y) => `translate(${x - 12 * PIN_SCALE}, ${y - 22.5 * PIN_SCALE}) scale(${PIN_SCALE})`;

const arc = (from, to, bend) => {
  const mx = (from.x + to.x) / 2 + bend[0];
  const my = (from.y + to.y) / 2 + bend[1];
  return `M${from.x},${from.y} Q${mx},${my} ${to.x},${to.y}`;
};

const ROUTES = [
  { id: 'route-1', from: STOPS[0], to: STOPS[1], path: arc(STOPS[0], STOPS[1], [-12, -24]) },
  { id: 'route-2', from: STOPS[1], to: STOPS[2], path: arc(STOPS[1], STOPS[2], [8, -20]) },
];

const KsaRoadshowMap = () => {
  const rootRef = useRef(null);
  const routeRefs = useRef({});
  const pulseRefs = useRef({});
  const dotRefs = useRef({});
  const labelRefs = useRef({});

  useEffect(() => {
    const ctx = gsap.context(() => {
      Object.values(routeRefs.current).forEach((path) => {
        if (!path) return;
        const length = path.getTotalLength();
        gsap.set(path, { strokeDasharray: length, strokeDashoffset: length, opacity: 1 });
      });
      Object.values(dotRefs.current).forEach((dot) => dot && gsap.set(dot, { opacity: 0, y: 0 }));
      Object.values(labelRefs.current).forEach((label) => label && gsap.set(label, { opacity: 0, y: 6 }));
      Object.values(pulseRefs.current).forEach((pulse) => pulse && gsap.set(pulse, { opacity: 0, scale: 0.4, transformOrigin: '50% 50%' }));

      const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.4, defaults: { ease: 'power2.out' } });

      tl.set(Object.values(routeRefs.current).filter(Boolean), { strokeDashoffset: (i, t) => t.getTotalLength() })
        .set(Object.values(dotRefs.current).filter(Boolean), { opacity: 0, y: 0 })
        .set(Object.values(labelRefs.current).filter(Boolean), { opacity: 0, y: 6 })
        .set(Object.values(pulseRefs.current).filter(Boolean), { opacity: 0, scale: 0.4 });

      const activateStop = (stopId, label) => {
        const pin = dotRefs.current[stopId];
        const pulse = pulseRefs.current[stopId];
        const lbl = labelRefs.current[stopId];

        // Land directly in place, no detour — then a gentle repeating float
        // and a soft sonar-style ripple (three fading pings) while it's the
        // active stop, instead of a single one-off blink.
        tl.to(pin, { opacity: 1, duration: 0.6, ease: 'sine.out' }, label)
          .to(pin, { y: -4, duration: 0.75, ease: 'sine.inOut', repeat: 3, yoyo: true }, `${label}+=0.1`)
          .fromTo(
            pulse,
            { opacity: 0.6, scale: 0.5 },
            { opacity: 0, scale: 2.2, duration: 1.1, ease: 'power1.out', repeat: 2, repeatDelay: 0.3 },
            label
          )
          .to(lbl, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, `${label}+=0.25`);
      };

      const drawRoute = (routeId, label) => {
        tl.to(routeRefs.current[routeId], { strokeDashoffset: 0, duration: 1.1, ease: 'power1.inOut' }, label);
      };

      activateStop('jeddah', 0);
      drawRoute('route-1', 1.4);
      activateStop('riyadh', 2.3);
      drawRoute('route-2', 3.7);
      activateStop('dammam', 4.6);

      // Hold the finished route on screen, then dissolve everything softly
      // before the loop restarts — no more instant, jarring reset.
      tl.to({}, { duration: 1.8 })
        .to(
          [
            ...Object.values(routeRefs.current).filter(Boolean),
            ...Object.values(dotRefs.current).filter(Boolean),
            ...Object.values(labelRefs.current).filter(Boolean),
          ],
          { opacity: 0, duration: 1, ease: 'power1.inOut' }
        );
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={rootRef}
      className="relative w-full max-w-md mx-auto"
      data-testid="ksa-roadshow-map"
    >
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="relative w-full h-auto overflow-visible"
        style={{ maxHeight: '320px' }}
        role="img"
        aria-label="Map of Saudi Arabia showing the WALLIX roadshow route from Jeddah to Riyadh to Dammam"
      >
        <defs>
          <clipPath id="ksa-clip">
            <path d={KSA_OUTLINE} />
          </clipPath>
          {/* Diagonal terrain shading: light catching the top-left, deepening toward the bottom-right */}
          <linearGradient id="ksa-land" x1="15%" y1="10%" x2="85%" y2="95%">
            <stop offset="0%" stopColor="#fffaf7" />
            <stop offset="45%" stopColor="hsl(var(--warm-gray-100))" />
            <stop offset="100%" stopColor="hsl(var(--warm-gray-300))" />
          </linearGradient>
          <radialGradient id="ksa-sheen" cx="28%" cy="22%" r="55%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="ksa-pin" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--brand-maroon-light))" />
            <stop offset="100%" stopColor="hsl(var(--brand-maroon-dark))" />
          </linearGradient>
          <filter id="ksa-pin-shadow" x="-60%" y="-60%" width="220%" height="220%">
            <feDropShadow dx="0" dy="1.5" stdDeviation="1.2" floodColor="hsl(var(--brand-maroon-dark))" floodOpacity="0.4" />
          </filter>
        </defs>

        {/* Country outline as a raised slab: a darker offset "side" face gives
            it real thickness, topped by the lit face + sheen. */}
        <path d={KSA_OUTLINE} fill="hsl(var(--warm-gray-400))" transform="translate(4, 9)" />
        <path
          d={KSA_OUTLINE}
          fill="url(#ksa-land)"
          stroke="hsl(var(--brand-maroon))"
          strokeOpacity="0.5"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <g clipPath="url(#ksa-clip)">
          <path d={KSA_OUTLINE} fill="url(#ksa-sheen)" />
        </g>

        {/* Route arcs (faint base line always visible + animated tracer on top) */}
        {ROUTES.map((route) => (
          <g key={route.id}>
            <path d={route.path} fill="none" stroke="hsl(var(--warm-gray-300))" strokeWidth="1.2" strokeDasharray="2 5" />
            <path
              ref={(el) => (routeRefs.current[route.id] = el)}
              d={route.path}
              fill="none"
              stroke="hsl(var(--brand-maroon))"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </g>
        ))}

        {/* City stops */}
        {STOPS.map((stop) => (
          <g key={stop.id}>
            <ellipse cx={stop.x} cy={stop.y + 2} rx="7" ry="2.2" fill="hsl(var(--brand-maroon-dark))" opacity="0.25" />
            <circle
              ref={(el) => (pulseRefs.current[stop.id] = el)}
              cx={stop.x}
              cy={stop.y}
              r="13"
              fill="none"
              stroke="hsl(var(--brand-maroon))"
              strokeWidth="1.6"
            />
            <g ref={(el) => (dotRefs.current[stop.id] = el)}>
              <path
                d={PIN_PATH}
                transform={pinTransform(stop.x, stop.y)}
                fill="url(#ksa-pin)"
                stroke="#ffffff"
                strokeWidth="0.75"
                filter="url(#ksa-pin-shadow)"
              />
            </g>
            <g ref={(el) => (labelRefs.current[stop.id] = el)}>
              <foreignObject x={stop.x - 65} y={stop.y - 62} width="130" height="40" style={{ overflow: 'visible' }}>
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  className="flex flex-col items-center text-center"
                >
                  <div className="inline-flex flex-col items-center gap-0 rounded-lg bg-white px-2.5 py-1 shadow-md border border-warm-gray-200">
                    <span className="text-[12px] font-bold text-foreground leading-tight whitespace-nowrap">{stop.name}</span>
                    <span className="text-[10px] font-semibold text-brand-maroon leading-tight whitespace-nowrap">{stop.date}</span>
                  </div>
                </div>
              </foreignObject>
            </g>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default KsaRoadshowMap;
